import {makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {ChangeEvent, MouseEvent, SyntheticEvent} from "react";
import {AxiosError} from "axios";
import {ViewController} from "data/types/structure";
import {Bindings} from "data/constants/bindings";
import type {IAuthController} from "views/controllers/auth/auth.controller";
import type {ICouponStore} from "data/stores/coupon/coupon.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {logoLinks, mobileLinks} from "data/constants";
import type {IUserStore} from "data/stores/user/user.store";
import {IAxiosApiError} from "data/types/api";
import {Language, ModalType} from "data/enums";
import logo from "assets/img/cfl-small-white-logo.png";
import logoFr from "assets/img/cfl-small-white-logo-french.png";

const icons: Record<string, string> = {
	en: logo,
	fr: logoFr,
};

interface IParams {
	language: string;
}

export interface IMobileMenuController extends ViewController<IParams> {
	get isShowCoupon(): boolean;
	get isOpenMenu(): boolean;
	get isAuthorized(): boolean;
	get value(): string;
	get language(): string;
	get icon(): string;
	get localeUrls(): (typeof mobileLinks)["en"];
	get logoLink(): string;

	showCouponHandler: () => void;
	setIsOpenMenu: (isOpen: boolean) => void;
	toggleMenuHandler: () => void;
	setValue: (e: ChangeEvent<HTMLInputElement>) => void;
	getSocialNetworkUrl: (url: Record<string, string>, lang: string) => string;
	logoutHandler: () => void;
	navClickHandler: (e: MouseEvent<HTMLDivElement>) => void;
	searchHandler: (e: SyntheticEvent<HTMLFormElement>) => void;
}

@injectable()
export class MobileMenuController implements IMobileMenuController {
	@observable private _isOpenMenu = false;
	@observable private _value = "";
	@observable private _language: Language = Language.English;

	get isOpenMenu() {
		return this._isOpenMenu;
	}

	get isAuthorized() {
		return this._authController.isAuthorized;
	}

	get coupon() {
		return this._couponStore.firstPickSubmissionCoupon;
	}

	get isShowCoupon() {
		return this.isAuthorized && Boolean(this.coupon);
	}

	get value() {
		return this._value;
	}

	get language() {
		return this._language;
	}

	get icon() {
		return icons[this.language] || logo;
	}

	get localeUrls() {
		return mobileLinks[this._language] || mobileLinks[Language.English];
	}

	get logoLink() {
		return logoLinks[this._language] || logoLinks[Language.English];
	}

	constructor(
		@inject(Bindings.AuthController) private _authController: IAuthController,
		@inject(Bindings.CouponStore) private _couponStore: ICouponStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	showCouponHandler = () => {
		this.setIsOpenMenu(false);
		this._modalsStore.showModal(ModalType.COUPON, this.coupon);
	};

	setIsOpenMenu = (isOpen: boolean) => {
		this._isOpenMenu = isOpen;
	};

	toggleMenuHandler = () => {
		this._isOpenMenu = !this._isOpenMenu;
	};

	setValue = (e: ChangeEvent<HTMLInputElement>) => {
		this._value = e.target.value;
	};

	getSocialNetworkUrl = (url: Record<string, string>, lang: string) => {
		return url[lang] || url[Language.English];
	};

	logoutHandler = async () => {
		try {
			this.toggleMenuHandler();
			await this._userStore.logout();
		} catch (e) {
			const error = e as AxiosError<IAxiosApiError, unknown>;
			this._modalsStore.showAxiosError(error);
		}
	};

	navClickHandler = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
	};

	searchHandler = (e: SyntheticEvent<HTMLFormElement>) => {
		e.preventDefault();
		const {search}: Record<string, HTMLInputElement> = e.currentTarget;

		window.location.href = `${this.logoLink}search/?q=${search.value}`;
	};

	init(params: IParams) {
		this._language = params.language as Language;
	}

	dispose() {
		return;
	}
}
